
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Star, Mail } from 'lucide-react';

interface SubscriptionCardProps {
  plan: {
    id: string;
    name: string;
    description: string;
    price_monthly: number;
    price_yearly: number;
    features: any;
    target_audience: string;
  };
  billingCycle: 'monthly' | 'yearly';
  onSubscribe?: (planId: string, billingCycle: 'monthly' | 'yearly') => void;
  isPopular?: boolean;
  isProcessing?: boolean;
  isCurrentPlan?: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  plan,
  billingCycle,
  onSubscribe,
  isPopular = true,
  isProcessing,
  isCurrentPlan
}) => {
  const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
  const monthlyPrice = billingCycle === 'yearly' ? plan.price_yearly / 12 : plan.price_monthly;

  const features = plan.features || {
    features: [
      'Career Map 360 - Complete career planning tool',
      'DreamBoard+ Vision Planning workspace',
      'Teen-Only Fellowship Programs access',
      'Pitch Playground PRO tools',
      '1:1 Monthly Mentor Power Hours',
      'Shadow a Founder Experience',
      'Verified Skill Portfolio Builder',
      'AI-Powered Career Coach',
      'Community Leader Badge',
      'Early Access to Events',
      'Premium Support'
    ]
  };

  const handleContactClick = () => {
    window.location.href = 'mailto:teenvisor@gmail.com?subject=TeenVisor Premium Subscription';
  };

  return (
    <div className="relative z-10">
      <Card className={`h-full border-[#FF8C00] shadow-lg overflow-hidden transition-all duration-300 ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}>
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <Badge className="bg-gradient-to-r from-[#FF8C00] to-orange-600 text-white px-3 py-1 shadow-lg">
            <Star className="h-3 w-3 mr-1" />
            Premium Plan
          </Badge>
        </div>

        <CardHeader className="text-center relative">
          <CardTitle className="text-xl bg-gradient-to-r from-[#FF8C00] to-orange-600 bg-clip-text text-transparent">
            {plan.name}
          </CardTitle>
          <CardDescription className="mt-2">{plan.description}</CardDescription>
          
          <div className="mt-4">
            <div className="flex items-baseline justify-center">
              <span className="text-3xl font-bold text-[#FF8C00]">
                ${price}
              </span>
              <span className="text-muted-foreground ml-1">
                /{billingCycle === 'yearly' ? 'year' : 'month'}
              </span>
            </div>
            
            {billingCycle === 'yearly' && (
              <div className="mt-2">
                <div className="text-sm text-muted-foreground">
                  ${monthlyPrice.toFixed(2)}/month when billed annually
                </div>
                <Badge variant="secondary" className="mt-1 bg-blue-50 text-blue-600">
                  Pay Once Per Year
                </Badge>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col">
          <ul className="space-y-3 mb-6 flex-1">
            {features.features?.map((feature: string, index: number) => (
              <li key={index} className="flex items-start group">
                <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0 transition-colors duration-200 group-hover:text-green-600" />
                <span className="text-sm transition-colors duration-200 group-hover:text-foreground/80">{feature}</span>
              </li>
            ))}
          </ul>

          <div className="space-y-3">
            <Button
              onClick={handleContactClick}
              disabled={isCurrentPlan}
              className={`w-full bg-gradient-to-r from-[#FF8C00] to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white transition-all duration-300 ${isCurrentPlan ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' : ''}`}
            >
              <Mail className="h-4 w-4 mr-2" />
              {isCurrentPlan ? 'Current Plan' : 'Contact to Subscribe'}
            </Button>
            
            <div className="text-center text-sm text-muted-foreground">
              Email: <strong className="text-[#FF8C00]">teenvisor@gmail.com</strong>
            </div>
            
            <div className="text-xs text-center text-muted-foreground bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-100">
              💡 Already paid? Contact us to activate your premium features!
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionCard;
